import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/layout/Layout';
import {
  getSummary,
  getDaily,
  getHourly,
  getWeekly,
  getMonthly,
} from '../api/report.api';
import type {
  ReportSummary,
  DailyReport,
  HourlyReport,
  WeeklyReport,
  MonthlyReport,
} from '../api/report.api';
import styles from './ReportPage.module.css';

function formatDateInput(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function getDefaultFrom(): string {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return formatDateInput(d);
}

function getDefaultTo(): string {
  return formatDateInput(new Date());
}

type TabKey = 'overview' | 'daily' | 'hourly' | 'weekly' | 'monthly';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'overview', label: '요약' },
  { key: 'daily', label: '일별' },
  { key: 'hourly', label: '시간대' },
  { key: 'weekly', label: '요일별' },
  { key: 'monthly', label: '월별' },
];

export default function ReportPage() {
  const { storeId } = useAuth();
  const [activeTab, setActiveTab] = useState<TabKey>('overview');
  const [from, setFrom] = useState(getDefaultFrom);
  const [to, setTo] = useState(getDefaultTo);
  const [hourlyDate, setHourlyDate] = useState(getDefaultTo);
  const [monthlyYear, setMonthlyYear] = useState(new Date().getFullYear());

  const [summary, setSummary] = useState<ReportSummary | null>(null);
  const [daily, setDaily] = useState<DailyReport[]>([]);
  const [hourly, setHourly] = useState<HourlyReport[]>([]);
  const [weekly, setWeekly] = useState<WeeklyReport[]>([]);
  const [monthly, setMonthly] = useState<MonthlyReport[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!storeId) return;
    setLoading(true);
    setError(null);
    try {
      switch (activeTab) {
        case 'overview': {
          const data = await getSummary(storeId, from, to);
          setSummary(data);
          break;
        }
        case 'daily': {
          const data = await getDaily(storeId, from, to);
          setDaily(data);
          break;
        }
        case 'hourly': {
          const data = await getHourly(storeId, hourlyDate);
          setHourly(data);
          break;
        }
        case 'weekly': {
          const data = await getWeekly(storeId, from, to);
          setWeekly(data);
          break;
        }
        case 'monthly': {
          const data = await getMonthly(storeId, monthlyYear);
          setMonthly(data);
          break;
        }
      }
    } catch (err: any) {
      setError(err.message || '데이터를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, [storeId, activeTab, from, to, hourlyDate, monthlyYear]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const maxDaily = useMemo(
    () => Math.max(...daily.map((d) => d.total), 1),
    [daily],
  );
  const maxWeekly = useMemo(
    () => Math.max(...weekly.map((w) => w.average), 1),
    [weekly],
  );
  const maxHourly = useMemo(
    () => Math.max(...hourly.map((h) => h.count), 1),
    [hourly],
  );

  function getHeatColor(count: number, max: number): string {
    if (max === 0) return '#f1f5f9';
    const ratio = count / max;
    if (ratio === 0) return '#f1f5f9';
    if (ratio < 0.25) return '#dbeafe';
    if (ratio < 0.5) return '#93c5fd';
    if (ratio < 0.75) return '#3b82f6';
    return '#1d4ed8';
  }

  function getHeatTextColor(count: number, max: number): string {
    if (max === 0) return '#64748b';
    const ratio = count / max;
    return ratio >= 0.5 ? '#ffffff' : '#1e293b';
  }

  return (
    <Layout title="통계">
      <div className={styles.container}>
        <div className={styles.tabs}>
          {TABS.map((tab) => (
            <button
              key={tab.key}
              className={`${styles.tab} ${activeTab === tab.key ? styles.active : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* 기간 선택 (탭에 따라 다름) */}
        {(activeTab === 'overview' ||
          activeTab === 'daily' ||
          activeTab === 'weekly') && (
          <div className={styles.dateRange}>
            <input
              type="date"
              className={styles.dateInput}
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
            <span className={styles.dateSep}>~</span>
            <input
              type="date"
              className={styles.dateInput}
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          </div>
        )}

        {activeTab === 'hourly' && (
          <div className={styles.dateRange}>
            <input
              type="date"
              className={styles.dateInput}
              value={hourlyDate}
              onChange={(e) => setHourlyDate(e.target.value)}
            />
          </div>
        )}

        {activeTab === 'monthly' && (
          <div className={styles.dateRange}>
            <select
              className={styles.dateInput}
              value={monthlyYear}
              onChange={(e) => setMonthlyYear(Number(e.target.value))}
            >
              <option value={2025}>2025</option>
              <option value={2026}>2026</option>
            </select>
          </div>
        )}

        {loading && <div className={styles.loading}>로딩 중...</div>}
        {error && <div className={styles.error}>{error}</div>}

        {/* 요약 탭 */}
        {!loading && !error && activeTab === 'overview' && summary && (
          <>
            <div className={styles.summaryGrid}>
              <div className={styles.summaryCard}>
                <div className={styles.summaryLabel}>총 대기</div>
                <div className={`${styles.summaryValue} ${styles.primary}`}>
                  {summary.totalWaitings.toLocaleString()}
                </div>
              </div>
              <div className={styles.summaryCard}>
                <div className={styles.summaryLabel}>입장률</div>
                <div className={`${styles.summaryValue} ${styles.success}`}>
                  {summary.totalWaitings > 0
                    ? (
                        (summary.totalSeated / summary.totalWaitings) *
                        100
                      ).toFixed(1)
                    : 0}
                  %
                </div>
              </div>
              <div className={styles.summaryCard}>
                <div className={styles.summaryLabel}>노쇼율</div>
                <div className={`${styles.summaryValue} ${styles.danger}`}>
                  {summary.noShowRate}%
                </div>
              </div>
              <div className={styles.summaryCard}>
                <div className={styles.summaryLabel}>평균 인원</div>
                <div className={styles.summaryValue}>
                  {summary.averagePartySize}명
                </div>
              </div>
            </div>
            <div className={styles.summaryGrid}>
              <div className={styles.summaryCard}>
                <div className={styles.summaryLabel}>입장</div>
                <div className={styles.summaryValue}>
                  {summary.totalSeated.toLocaleString()}
                </div>
              </div>
              <div className={styles.summaryCard}>
                <div className={styles.summaryLabel}>노쇼</div>
                <div className={styles.summaryValue}>
                  {summary.totalNoShow.toLocaleString()}
                </div>
              </div>
              <div className={styles.summaryCard}>
                <div className={styles.summaryLabel}>취소</div>
                <div className={styles.summaryValue}>
                  {summary.totalCancelled.toLocaleString()}
                </div>
              </div>
              <div className={styles.summaryCard}>
                <div className={styles.summaryLabel}>일평균</div>
                <div className={styles.summaryValue}>
                  {summary.averageDailyWaitings}건
                </div>
              </div>
            </div>

            {/* 신규/재방문 분석 */}
            {((summary.newCustomers ?? 0) + (summary.returningCustomers ?? 0)) > 0 && (
              <div className={styles.summaryGrid}>
                <div className={styles.summaryCard}>
                  <div className={styles.summaryLabel}>신규 고객</div>
                  <div className={styles.summaryValue}>
                    {(summary.newCustomers ?? 0).toLocaleString()}
                    <span style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)', marginLeft: 4 }}>
                      / {(summary.newVisits ?? 0).toLocaleString()}회 방문
                    </span>
                  </div>
                </div>
                <div className={styles.summaryCard}>
                  <div className={styles.summaryLabel}>재방문 고객</div>
                  <div className={`${styles.summaryValue} ${styles.primary}`}>
                    {(summary.returningCustomers ?? 0).toLocaleString()}
                    <span style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)', marginLeft: 4 }}>
                      / {(summary.returningVisits ?? 0).toLocaleString()}회 방문
                    </span>
                  </div>
                </div>
                <div className={styles.summaryCard}>
                  <div className={styles.summaryLabel}>재방문율</div>
                  <div className={`${styles.summaryValue} ${styles.success}`}>
                    {summary.returningRate ?? 0}%
                  </div>
                </div>
                <div className={styles.summaryCard}>
                  <div className={styles.summaryLabel}>고객 합계</div>
                  <div className={styles.summaryValue}>
                    {((summary.newCustomers ?? 0) + (summary.returningCustomers ?? 0)).toLocaleString()}
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* 일별 탭 */}
        {!loading && !error && activeTab === 'daily' && daily.length > 0 && (
          <div className={styles.section}>
            <div className={styles.sectionTitle}>일별 대기 건수</div>
            <div className={styles.dailyChartScroll}>
              <div className={styles.dailyChart}>
                {daily.map((d) => (
                  <div key={d.date} className={styles.dailyBar} title={`${d.date}: ${d.total}건`}>
                    <span className={styles.dailyBarCount}>{d.total}</span>
                    <div
                      className={styles.dailyBarFill}
                      style={{
                        height: `${(d.total / maxDaily) * 100}%`,
                      }}
                    />
                    <span className={styles.dailyBarLabel}>
                      {d.date.slice(5)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* 시간대 탭 */}
        {!loading && !error && activeTab === 'hourly' && hourly.length > 0 && (
          <div className={styles.section}>
            <div className={styles.sectionTitle}>
              시간대별 대기 건수 ({hourlyDate})
            </div>
            <table className={styles.heatmapTable}>
              <thead>
                <tr>
                  <th>시간</th>
                  <th>건수</th>
                  <th>분포</th>
                </tr>
              </thead>
              <tbody>
                {hourly
                  .filter((h) => h.hour >= 9 && h.hour <= 22)
                  .map((h) => (
                    <tr key={h.hour}>
                      <td>{h.hour}시</td>
                      <td>
                        <span
                          className={styles.heatmapCell}
                          style={{
                            background: getHeatColor(h.count, maxHourly),
                            color: getHeatTextColor(h.count, maxHourly),
                            padding: '2px 8px',
                            borderRadius: '4px',
                            display: 'inline-block',
                            minWidth: '36px',
                          }}
                        >
                          {h.count}
                        </span>
                      </td>
                      <td>
                        <div className={styles.barTrack} style={{ height: '14px' }}>
                          <div
                            className={styles.barFill}
                            style={{
                              width: `${maxHourly > 0 ? (h.count / maxHourly) * 100 : 0}%`,
                              height: '100%',
                            }}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}

        {/* 요일별 탭 */}
        {!loading && !error && activeTab === 'weekly' && weekly.length > 0 && (
          <div className={styles.section}>
            <div className={styles.sectionTitle}>요일별 평균 대기 건수</div>
            <div className={styles.weeklyChart}>
              {weekly.map((w) => (
                <div key={w.dayOfWeek} className={styles.weeklyBar}>
                  <span className={styles.weeklyBarValue}>{w.average}</span>
                  <div
                    className={styles.weeklyBarFill}
                    style={{
                      height: `${(w.average / maxWeekly) * 80}%`,
                    }}
                  />
                  <span className={styles.weeklyBarLabel}>{w.dayName}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 월별 탭 */}
        {!loading && !error && activeTab === 'monthly' && monthly.length > 0 && (
          <div className={styles.section}>
            <div className={styles.sectionTitle}>{monthlyYear}년 월별 통계</div>
            <table className={styles.monthlyTable}>
              <thead>
                <tr>
                  <th>월</th>
                  <th>대기 건수</th>
                  <th>노쇼율</th>
                  <th>평균 인원</th>
                </tr>
              </thead>
              <tbody>
                {monthly.map((m) => (
                  <tr key={m.month}>
                    <td>{m.month}월</td>
                    <td>{m.totalWaitings.toLocaleString()}</td>
                    <td>{m.noShowRate}%</td>
                    <td>{m.averagePartySize}명</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}
